import { useMutation } from '@apollo/client';
import { getInvestmentBands, maximumPrice } from '@seek/sbiz-taxonomies';
import {
  Checkbox,
  Column,
  Columns,
  Dropdown,
  Heading,
  Stack,
} from 'braid-design-system';
import lodashMax from 'lodash/max';
import { Fragment, useEffect, useState } from 'react';

import LayoutBox from 'src/components/LayoutBox/LayoutBox';
import { formatPriceBand } from 'src/utils/investmentBands';

import UPDATE_PREFERENCES_EXCLUDE_POA from './queries/updatePreferencesExcludePoa';
import UPDATE_PREFERENCES_PRICE from './queries/updatePreferencesPrice';

interface Props {
  price?: { min: number; max: number };
  excludePoa: boolean;
}

// Not every language has the exact same value for their equivalent of MAX_SAFE_INTEGER
const normaliseMaxPrice = (price?: number) => {
  if (!price || price > lodashMax(getInvestmentBands())!) {
    return maximumPrice;
  }
  return price;
};

const PriceRange = ({
  price = { min: 0, max: maximumPrice },
  excludePoa,
}: Props) => {
  const [excludePoaState, setExcludePoa] = useState(false);
  const [maxPrice, setMaxPrice] = useState(maximumPrice);
  const [minPrice, setMinPrice] = useState(0);

  const [updatePreferencesPrice] = useMutation(UPDATE_PREFERENCES_PRICE);

  useEffect(() => {
    if (price) {
      setMinPrice(price.min);
      setMaxPrice(normaliseMaxPrice(price.max));
    }
  }, [price]);

  const submitPrice = (min: number, max: number) => {
    updatePreferencesPrice({
      variables: {
        input: { min, max },
      },
    });
    setMinPrice(min);
    setMaxPrice(max);
  };

  const [updatePreferencesExcludePoa] = useMutation(
    UPDATE_PREFERENCES_EXCLUDE_POA,
  );
  useEffect(() => {
    setExcludePoa(excludePoa);
  }, [excludePoa]);

  const submitExcludePoa = (value: boolean) => {
    updatePreferencesExcludePoa({
      variables: {
        input: value,
      },
    });
    setExcludePoa(value);
  };

  return (
    <Stack space="medium">
      <Heading level="4">Price Range</Heading>

      <Stack space="small">
        <Columns space="small" collapseBelow="tablet">
          <Column width="content">
            <LayoutBox maxWidth={300}>
              <Dropdown
                id="minPrice"
                value={minPrice}
                onChange={(e) =>
                  submitPrice(Number(e.currentTarget.value), maxPrice)
                }
                placeholder={minPrice ? undefined : 'Select a minimum price'}
                label="From"
              >
                {getInvestmentBands().map((o, k) => (
                  <option key={k} value={o} disabled={o > maxPrice}>
                    {formatPriceBand(o)}
                  </option>
                ))}
              </Dropdown>
            </LayoutBox>
          </Column>

          <Column width="content">
            <LayoutBox maxWidth={300}>
              <Dropdown
                id="maxPrice"
                value={maxPrice}
                onChange={(e) =>
                  submitPrice(minPrice, Number(e.currentTarget.value))
                }
                placeholder={maxPrice ? undefined : 'Select a maximum price'}
                label="To"
              >
                <Fragment>
                  {getInvestmentBands().map((o, k) => (
                    <option key={k} value={o} disabled={o < minPrice}>
                      {formatPriceBand(o)}
                    </option>
                  ))}
                  <option key={maximumPrice} value={maximumPrice}>
                    Over $2,000,000
                  </option>
                </Fragment>
              </Dropdown>
            </LayoutBox>
          </Column>
        </Columns>

        <Checkbox
          label="Exclude P.O.A ads"
          id="excludePoa"
          checked={excludePoaState}
          onChange={(e) => {
            submitExcludePoa(e.currentTarget.checked);
          }}
        />
      </Stack>
    </Stack>
  );
};

export default PriceRange;
